<script lang="ts" setup>
import * as _ from 'lodash-es'
import * as dateFns from 'date-fns'
import FloatLabel from 'primevue/floatlabel'
import MultiSelect from 'primevue/multiselect'
import { formatList } from '@/lib/helpers'

const filterInputStore = useFilterInputStore()
const optionsStore = useOptionsStore()

const clonedFilterData = useClonedObject(filterInputStore.values)

function onAddPosition() {
  clonedFilterData.data.positions.push({
    uid: crypto.randomUUID(),
    position: [],
    date: [filterInputStore.minDate, filterInputStore.maxDate],
  })
}

function onRemovePosition(uid: string) {
  clonedFilterData.data.positions = clonedFilterData.data.positions.filter(p => p.uid !== uid)
}

function onCancel() {
  clonedFilterData.cancel()
}

function onCommit() {
  clonedFilterData.commit()
}

onMounted(() => {
  if (clonedFilterData.data.positions.length === 0) {
    onAddPosition()
  }
})

const options = ['positions', 'partyAffiliation'] as const

const isActive = computed(() => options.some(option => filterInputStore.isOptionActive[option]))

function onClear() {
  filterInputStore.reset({ include: options })
}
</script>

<template>
  <FilterGroup
    name="Amt oder Stellung"
    :active="isActive"
    @cancel="onCancel"
    @commit="onCommit"
    @clear="onClear"
  >
    <template #icon>
      <!-- prettier-ignore -->
      <svg width="20" height="20" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M362.5 26.5v60h153v399h-519v-399h153v-60h213Zm-329 260v162h445v-162a144.2 144.2 0 0 1-107.6 48h-46.4v30h-137v-30H141a144 144 0 0 1-106.8-47.1l-.8-1Zm254 18h-63v23h63v-23Zm191-181h-445V190c0 58.9 47.2 106.7 105.8 107.6h48.2v-30h137v30H371c58.8 0 106.7-47.2 107.6-105.8v-68.2Zm-153-60h-139v23h139v-23Z"/></svg>
    </template>
    <template #body>
      <div>
        <div>
          <div v-for="(position, index) in clonedFilterData.data.positions">
            <div v-if="index > 0">
              <hr data-label="und" />
            </div>

            <div class="position-container">
              <div class="position">
                <div style="align-self: center">Eines dieser Ämter:</div>
                <FloatLabel>
                  <MultiSelect
                    v-model="position.position"
                    :options="optionsStore.positions"
                    optionLabel="label"
                    display="chip"
                    placeholder=""
                    :filter="optionsStore.positions.length > 10"
                  />
                  <label>Amt</label>
                </FloatLabel>
                <div>In dieser Zeitspanne:</div>
                <DateRangeInput
                  primary-aspect="date"
                  :slider-steps="25"
                  :min-date="filterInputStore.minDate"
                  :max-date="filterInputStore.maxDate"
                  :close-on-escape="false"
                  v-model="position.date"
                />
              </div>

              <Button
                label="Entfernen"
                size="small"
                link
                class="single"
                @click="onRemovePosition(position.uid)"
              >
                <template #icon="scope">
                  <!-- prettier-ignore -->
                  <svg :class="scope.class" viewBox="0 0 24 24" width="20" height="20"><path d="M13.06,12l4.42-4.42a.75.75,0,1,0-1.06-1.06L12,10.94,7.58,6.52A.75.75,0,0,0,6.52,7.58L10.94,12,6.52,16.42a.75.75,0,0,0,0,1.06.75.75,0,0,0,1.06,0L12,13.06l4.42,4.42a.75.75,0,0,0,1.06,0,.75.75,0,0,0,0-1.06Z" fill="currentColor"/></svg>
                </template>
              </Button>
            </div>
          </div>
          <Button
            class="add-position"
            :label="
              clonedFilterData.data.positions.length === 0
                ? 'Amt hinzufügen'
                : 'Weiteres Amt hinzufügen'
            "
            text
            @click="onAddPosition"
          >
            <template #icon="scope">
              <!-- prettier-ignore -->
              <svg :class="scope.class" viewBox="0 0 24 24" width="20" height="20"><path d="M12.8 11.3V5a.8.8 0 0 0-1.6 0v6.3H5a.8.8 0 0 0 0 1.4h6.3V19a.8.8 0 0 0 .7.8.8.8 0 0 0 .8-.8v-6.3H19a.8.8 0 0 0 .8-.7.8.8 0 0 0-.8-.8Z" fill="currentColor"/></svg>
            </template>
          </Button>
        </div>
      </div>
      <FloatLabel>
        <MultiSelect
          v-model="clonedFilterData.data.partyAffiliation"
          :options="optionsStore.parties"
          optionLabel="name"
          display="chip"
          placeholder=""
          :filter="optionsStore.parties.length > 10"
        />
        <label>Parteizugehörigkeit</label>
      </FloatLabel>
    </template>
    <template #summary>
      <div
        v-if="
          filterInputStore.isOptionActive.positions &&
          filterInputStore.values.positions.some(positionItem => positionItem.position.length > 0)
        "
      >
        <ul style="margin-top: 0; padding-left: 0.875em">
          <li
            v-for="position in filterInputStore.values.positions.filter(
              positionItem => positionItem.position.length > 0,
            )"
          >
            <strong>{{ formatList(_.map(position.position, 'label'), 'disjunction') }}</strong>
            <template
              v-if="
                !filterInputStore.isMinDate(position.date[0]) ||
                !filterInputStore.isMaxDate(position.date[1])
              "
            >
              zwischen
              {{ dateFns.format(position.date[0] ?? filterInputStore.minDate, 'dd.MM.yyyy') }} und
              {{ dateFns.format(position.date[1] ?? filterInputStore.maxDate, 'dd.MM.yyyy') }}
            </template>
          </li>
        </ul>
      </div>
      <div v-if="filterInputStore.isOptionActive.partyAffiliation">
        Partei:
        <strong>{{ formatList(_.map(filterInputStore.values.partyAffiliation, 'name')) }}</strong>
      </div>
    </template>
  </FilterGroup>
</template>

<style scoped>
.position-container {
  padding: 2rem 1rem 1rem;
  background: var(--bluegray-50);
  border-radius: 3px;
}

.position {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
}

.add-position {
  margin-bottom: 1.5rem;

  &:not(:first-child) {
    margin-top: 1.5rem;
  }
}
</style>
